import { Link } from "react-router-dom";
import { Calendar, MapPin, Users } from "lucide-react";
import LikeButton from "../LikeButton";
import { budgetLabel, durationChip, monthYear } from "./tripCardView";

// Same last-resort pictures as PostCard: a post with no approved photo gets one made for its destination, so a row of photo-less
// cards does not look like one card repeated.
const GENERIC_FALLBACK_IMAGE = "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=80";
const fallbackImageFor = (destination) =>
  destination
    ? `https://image.pollinations.ai/prompt/${encodeURIComponent(`${destination} famous landmark landscape photography 8k resolution`)}?width=800&height=500&nologo=true`
    : GENERIC_FALLBACK_IMAGE;

/**
 * A trip as a card for the home page's rows: the photo with the trip's length and a heart on it, then the destination, where it is,
 * a line of what the traveller wrote, and a footer of who went, when, and the trip budget. The budget is the traveller's own
 * figure for the whole trip, so it is labelled as that — not "per person".
 *
 * The heart is a sibling of the <Link>, not inside it, so it is a real button and does not need to stop the card navigating.
 */
const TripCard = ({ post }) => {
  const { slug, trip, coverPhoto, review } = post;
  const title = trip?.destination || "Untitled trip";
  const place = [trip?.destination !== title ? trip?.destination : null, trip?.country].filter(Boolean).join(", ");
  const length = durationChip(trip);
  const when = monthYear(trip);
  const budget = budgetLabel(trip);
  const note = review?.title?.trim();

  return (
    <article className="group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl">
      <Link
        to={`/community/${slug}`}
        className="flex flex-1 flex-col focus:outline-none focus-visible:ring-4 focus-visible:ring-teal-500/40"
      >
        <div className="relative aspect-[16/8] w-full overflow-hidden bg-gray-100">
          <img
            src={coverPhoto?.cardUrl || coverPhoto?.publicUrl || fallbackImageFor(trip?.destination)}
            alt={trip?.destination || title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          {/* A soft dark fade along the top of the photo, so the white heart (which has no background of its own) shows on a light picture too. */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/40 to-transparent" aria-hidden="true" />
          {length && (
            <span className="absolute left-3 top-3 rounded-full bg-gray-900/80 px-2.5 py-1 text-[11px] font-bold tracking-wide text-white backdrop-blur-sm">
              {length}
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col px-4 pb-3 pt-3.5">
          <h3 className="line-clamp-1 text-[15px] font-bold leading-snug text-gray-900 group-hover:text-teal-700">{title}</h3>
          {place && (
            <p className="mt-1 flex items-center gap-1 truncate text-xs font-medium text-gray-600">
              <MapPin size={12} className="shrink-0 text-teal-600" aria-hidden="true" />
              <span className="truncate">{place}</span>
            </p>
          )}
          <p className="mt-2 line-clamp-2 min-h-[2.25rem] text-xs leading-snug text-gray-500">
            {note || "A trip shared by a fellow traveller."}
          </p>

          <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs text-gray-600">
            <span className="flex min-w-0 items-center gap-1.5">
              <Users size={14} className="shrink-0 text-teal-600" aria-hidden="true" />
              <span className="truncate">{trip?.tripType || "Trip"}</span>
            </span>
            {when && (
              <span className="flex shrink-0 items-center gap-1.5">
                <Calendar size={14} className="shrink-0 text-teal-600" aria-hidden="true" />
                {when}
              </span>
            )}
            {budget && (
              <span className="flex shrink-0 flex-col items-end leading-none">
                <span className="text-[13px] font-bold text-gray-900">{budget}</span>
                <span className="mt-0.5 text-[9px] font-medium text-gray-400">trip budget</span>
              </span>
            )}
          </div>
        </div>
      </Link>

      <div className="absolute right-3 top-3 flex items-center">
        <LikeButton post={post} variant="overlay" />
      </div>
    </article>
  );
};

export default TripCard;
